import React from "react";
import dayjs from "@/config/dayjs";
import { withTranslation, type WithTranslation } from "react-i18next";
import RichText from "../Common/RichText";
import { ActionIcon, Button, Checkbox } from "../ui";

export interface NotificationCardItem {
  id: number | string;
  title?: string;
  content?: string;
  date?: string | number | Date;
  targetPath?: string;
  read?: boolean | number | string;
}

interface NotificationListProps extends WithTranslation {
  notifications: NotificationCardItem[];
  onRead: (notificationId: number | string) => void;
  onDelete: (notificationId: number | string) => void;
  onOpen?: (notification: NotificationCardItem) => void;
  selectedNotificationIds: string[];
  onSelectionChange: (notificationId: number | string, selected: boolean) => void;
}

function isUnread(notification: NotificationCardItem) {
  return notification.read !== true && notification.read !== 1 && notification.read !== "1";
}

function NotificationList({
  t,
  i18n,
  notifications,
  onRead,
  onDelete,
  onOpen,
  selectedNotificationIds,
  onSelectionChange,
}: NotificationListProps) {
  dayjs.locale((i18n && i18n.resolvedLanguage) || "fr");

  return (
    <section className="NotificationPage-list">
      <ul className="NotificationPage-listInner">
        {notifications.map((notification) => {
          const unread = isUnread(notification);
          const isClickable = !!notification.targetPath;
          const ContentTag = (isClickable ? "button" : "div") as React.ElementType;
          const selected = selectedNotificationIds.includes(String(notification.id));

          return (
            <li
              className={`NotificationCard${unread ? " NotificationCard--unread" : " NotificationCard--read"}${isClickable ? " NotificationCard--clickable" : ""}`}
              key={notification.id}
            >
              <div className="NotificationCard-selection">
                <Checkbox
                  checked={selected}
                  onChange={(event) => onSelectionChange(notification.id, event.target.checked)}
                  aria-label={t("notifications.select")}
                />
              </div>
              <ContentTag
                type={isClickable ? "button" : undefined}
                className="NotificationCard-dataBlock"
                onClick={isClickable && onOpen ? () => onOpen(notification) : undefined}
              >
                <div className="NotificationCard-topline">
                  <span
                    className={`NotificationCard-status NotificationCard-status--${unread ? "unread" : "read"}`}
                    aria-label={
                      unread ? t("notifications.status_unread") : t("notifications.status_read")
                    }
                    title={
                      unread ? t("notifications.status_unread") : t("notifications.status_read")
                    }
                  />
                  <p className="NotificationCard-title">{notification.title}</p>
                </div>
                <RichText className="NotificationCard-content" html={notification.content} />
                <p className="NotificationCard-date">{dayjs(notification.date).fromNow()}</p>
              </ContentTag>
              <div className="NotificationCard-actions">
                {unread ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="Button NotificationCard-action NotificationCard-action--icon NotificationCard-action--secondary"
                    onClick={() => onRead(notification.id)}
                    aria-label={t("notifications.mark_as_read")}
                    title={t("notifications.mark_as_read")}
                  >
                    <ActionIcon name="markRead" />
                  </Button>
                ) : null}
                <Button
                  variant="danger"
                  size="sm"
                  className="Button NotificationCard-action NotificationCard-action--icon NotificationCard-action--danger"
                  onClick={() => onDelete(notification.id)}
                  aria-label={t("notifications.delete")}
                  title={t("notifications.delete")}
                >
                  <ActionIcon name="delete" />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default withTranslation()(NotificationList);
